import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, User, Calendar } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const InvoiceDescriptionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania faktury');
      console.error('Error fetching invoice:', error);
      navigate('/app/invoices/list');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-gray-500">Faktura nie została znaleziona</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Opis faktury</h2>
              <p className="text-sm text-gray-600 mt-1">Numer faktury: {invoice.number}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body">
        {invoice.description || invoice.signatureImage ? (
          <div className="space-y-6">
            {/* Informacje o fakturze */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Dostawca:</span>
                  <span className="ml-2 font-medium text-gray-900">{invoice.supplierName || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-600">NIP:</span>
                  <span className="ml-2 font-medium text-gray-900">{invoice.supplierNip || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Data wystawienia:</span>
                  <span className="ml-2 font-medium text-gray-900">{formatDate(invoice.issueDate)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Termin płatności:</span>
                  <span className="ml-2 font-medium text-gray-900">{formatDate(invoice.dueDate)}</span>
                </div>
              </div>
            </div>

            {/* Opis faktury */}
            {invoice.description && (
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900">Opis faktury</h3>
                </div>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{invoice.description}</p>
                </div>
              </div>
            )}

            {/* Stanowisko */}
            {invoice.signedByPosition && (
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-5 w-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900">Stanowisko</h3>
                </div>
                <p className="text-gray-700">{invoice.signedByPosition}</p>
              </div>
            )}

            {/* Podpis */}
            {invoice.signatureImage && (
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Podpis</h3>
                <div className="flex justify-center">
                  <img 
                    src={invoice.signatureImage} 
                    alt="Podpis" 
                    className="max-w-md border border-gray-300 rounded-lg shadow-sm"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-16 w-16 text-gray-300" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Brak opisu</h3>
            <p className="mt-2 text-sm text-gray-500">Ta faktura nie ma jeszcze opisu ani podpisu.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDescriptionView;
